import React, { useState } from 'react';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { AlertTriangle, CreditCard, Shield } from 'lucide-react';
import { logger } from '@/utils/logger';
import type { StripePaymentData } from '@/types/batch';

interface BatchPaymentAuthFormProps {
  onSubmit: (e: React.FormEvent) => void;
  stripeData: StripePaymentData;
  isSubmitting: boolean;
  savePaymentMethod: boolean;
  onSavePaymentMethodChange: (save: boolean) => void;
}

export const BatchPaymentAuthForm: React.FC<BatchPaymentAuthFormProps> = ({
  onSubmit,
  isSubmitting,
  savePaymentMethod,
  onSavePaymentMethodChange,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [elementError, setElementError] = useState('');

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!stripe || !elements) {
      setElementError('Stripe has not finished loading. Please wait a moment and try again.');
      return;
    }

    setElementError('');

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        payment_method_data: {},
      },
      redirect: 'if_required',
    });

    if (error) {
      logger.error('Stripe payment confirmation failed', error);
      setElementError(error.message ?? 'Unable to authorize payment. Please check your details and try again.');
      return;
    }

    onSubmit(event);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="ca-card ca-card-p ">
        <h2 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <CreditCard className="w-4 h-4" />
          Payment Method
        </h2>
        <div className="border border-slate-200 rounded-md p-3">
          <PaymentElement
            onChange={() => setElementError('')}
            options={{ layout: 'tabs' }}
          />
        </div>
        {elementError && (
          <div className="mt-3 text-sm text-red-600 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            {elementError}
          </div>
        )}

        <div className="mt-4">
          <label className="flex items-start gap-3 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={savePaymentMethod}
              onChange={(e) => onSavePaymentMethodChange(e.target.checked)}
              className="mt-0.5 w-4 h-4 text-slate-600 border-slate-300 rounded focus:ring-blue-500 flex-shrink-0"
            />
            <span>
              <span className="font-medium text-slate-900 block">Save this payment method for future batches</span>
              <span className="text-xs text-slate-500">Card details stay with Stripe. You can manage saved cards from your dashboard anytime.</span>
            </span>
          </label>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={isSubmitting}
          className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed hover:bg-blue-700"
        >
          {isSubmitting ? (
            <>
              <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              Authorizing...
            </>
          ) : (
            <>
              <Shield className="w-4 h-4" />
              Authorize & Start Processing
            </>
          )}
        </button>
      </div>
    </form>
  );
};
